/**
 * The now-playing bar (Sam, 2026-10-23): Spotify's own small player docked above the tab bar while a row's play button
 * is playing. Its play/pause and the row's are one state (spotifyPlayerPool). A b2b set shows its artists as tabs over
 * the player and plays one at a time. While it is up the page gets that much more room at the bottom, so the last row
 * can always be scrolled clear of it - nothing scrolls by itself.
 *
 * One bar for the app (Sam, 2026-10-27), in Layout: a preview keeps playing across the music pages - Events, an event's
 * page, Sets - and closes on any other (Maps and Find need the bottom of the screen; Groups, Profile and the rest are not
 * about music). It used to belong to each page, so opening the event you were previewing stopped it.
 */
import { createContext, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import {
  barSlotFor,
  closeBar,
  getBar,
  onPreviewOffline,
  onSlotChange,
  placeSlot,
  playInBar,
  slotReady,
  subscribeBar,
} from '../../lib/spotifyPlayerPool';
import { useToast } from '../../contexts/ToastContext';

/** True where rows carry a play button: the page that renders the bar provides it. */
export const PlayRowsContext = createContext(false);

export function useNowPlaying() {
  return useSyncExternalStore(subscribeBar, getBar, getBar);
}

/** Where a preview may keep playing. */
export function isMusicPage(pathname) {
  return pathname === '/' || pathname.startsWith('/events') || pathname.startsWith('/sets');
}

// A page can tuck the bar away while something covers the bottom of the screen (Sets' artist sheet): useHideNowPlaying.
const hiders = new Set();
const hideListeners = new Set();
const emitHide = () => hideListeners.forEach((l) => l());
const subscribeHide = (l) => {
  hideListeners.add(l);
  return () => hideListeners.delete(l);
};
const getHidden = () => hiders.size > 0;
export function useHideNowPlaying(on) {
  useEffect(() => {
    if (!on) return undefined;
    const token = {};
    hiders.add(token);
    emitHide();
    return () => {
      hiders.delete(token);
      emitHide();
    };
  }, [on]);
}

/** The app's one bar (Layout): closes when you go to a page that is not a music page. */
export function AppNowPlayingBar({ pathname }) {
  const music = isMusicPage(pathname);
  useEffect(() => {
    if (!music) closeBar();
  }, [music]);
  const hidden = useSyncExternalStore(subscribeHide, getHidden, getHidden);
  const { showToast } = useToast();
  useEffect(
    () => onPreviewOffline(() => showToast('No connection for previews', 2500)),
    [showToast],
  );
  return <NowPlayingBar hidden={hidden || !music} />;
}

export default function NowPlayingBar({ hidden = false }) {
  const bar = useNowPlaying();
  const frameRef = useRef(null);
  const boxRef = useRef(null);
  const up = Boolean(bar.key);

  useEffect(() => () => closeBar(), []); // leaving the page stops the music

  useEffect(() => {
    const el = frameRef.current;
    if (!up || hidden || !el) return undefined;
    const slot = barSlotFor();
    let raf = 0;
    const tick = () => {
      placeSlot(slot, el);
      raf = requestAnimationFrame(tick);
    };
    tick();
    const off = onSlotChange(slot, () => placeSlot(slot, el));
    return () => {
      cancelAnimationFrame(raf);
      off();
      slot.host.style.visibility = 'hidden';
      slot.host.style.pointerEvents = 'none';
    };
  }, [up, hidden]);

  // The page's extra bottom room is the bar's own height (taller with a b2b's tabs).
  useLayoutEffect(() => {
    const root = document.documentElement;
    const h = up && boxRef.current ? boxRef.current.getBoundingClientRect().height + 12 : 0;
    root.style.setProperty('--now-playing-h', `${Math.round(h)}px`);
    return () => root.style.setProperty('--now-playing-h', '0px');
  }, [up, bar.artists.length]);

  if (!up) return null;
  const ready = slotReady(barSlotFor());
  const current = bar.artists[bar.index];
  const tabs = bar.artists.length > 1 && (
    <div className="now-playing-bar__tabs" role="tablist">
      {bar.artists.map((a, i) => (
        <button
          key={a.spotifyId}
          type="button"
          role="tab"
          aria-selected={i === bar.index}
          className={`now-playing-bar__tab${i === bar.index ? ' now-playing-bar__tab--on' : ''}`}
          onClick={() => i !== bar.index && playInBar(bar.key, bar.artists, i)}
        >
          {a.name}
        </button>
      ))}
    </div>
  );
  const frame = (
    <div ref={frameRef} className="now-playing-bar__frame">
      {!ready && <div className="now-playing-bar__loading">Loading preview…</div>}
    </div>
  );
  return createPortal(
    <div
      ref={boxRef}
      className={`now-playing-bar${hidden ? ' now-playing-bar--hidden' : ''}`}
      role="region"
      aria-label="Now playing"
    >
      {/* A header line above Spotify's player (Sam's A, 2026-10-23): what is playing, or a b2b's tabs, and our close -
          clear of Spotify's own X and logo, which sit on the player itself. */}
      <div className="now-playing-bar__strip">
        {tabs || (
          <span className="now-playing-bar__title">
            <span>Now playing</span> {current?.name}
          </span>
        )}
        <button
          type="button"
          className="now-playing-bar__close"
          aria-label="Close player"
          onClick={closeBar}
        >
          <X size={16} strokeWidth={2.4} aria-hidden />
        </button>
      </div>
      {frame}
    </div>,
    document.body,
  );
}
